import type { NotificationRecord } from '@billing/validation';
import { useTranslation } from '@refinedev/core';
import { ActionIcon, Group, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { IconCircleCheck, IconCircleDashed } from '@tabler/icons-react';
import { DateTime } from 'luxon';
import { useCurrentSiteStore } from 'store/SiteStore';
import { getNotificationText } from './notificationText';
import { ReportNotificationActions } from './ReportNotificationActions';

interface NotificationListItemProps {
  notification: NotificationRecord;
  onOpen: (notification: NotificationRecord) => void;
  onToggleRead: (notification: NotificationRecord) => void;
  onReportPreview?: (url: string) => void;
  compact?: boolean;
}

export const NotificationListItem = ({
  notification,
  onOpen,
  onToggleRead,
  onReportPreview,
  compact = false,
}: NotificationListItemProps) => {
  const { translate } = useTranslation();
  const timezone = useCurrentSiteStore((state) => state.timezone) || 'UTC';
  const text = getNotificationText(translate, notification);
  const createdAt = DateTime.fromISO(notification.createdAt, {
    zone: 'utc',
  }).setZone(timezone);

  return (
    <Paper
      withBorder
      p={compact ? 'xs' : 'md'}
      bg={notification.readAt ? undefined : 'var(--mantine-color-blue-light)'}
    >
      <Group wrap="nowrap" align="flex-start" justify="space-between">
        <Stack
          gap={3}
          style={{ cursor: 'pointer', flex: 1 }}
          onClick={() => onOpen(notification)}
        >
          <Text
            fw={notification.readAt ? 500 : 700}
            size={compact ? 'sm' : 'md'}
          >
            {text.title}
          </Text>
          <Text size="sm" c="dimmed" lineClamp={compact ? 2 : undefined}>
            {text.body}
          </Text>
          <Text size="xs" c="dimmed">
            {createdAt.isValid
              ? createdAt.toLocaleString(DateTime.DATETIME_MED)
              : notification.createdAt}
          </Text>
          {notification.type === 'report.ready' && (
            <ReportNotificationActions
              reportID={notification.data.reportID}
              onPreview={onReportPreview}
              onMarkRead={() => {
                if (!notification.readAt) {
                  onToggleRead(notification);
                }
              }}
            />
          )}
        </Stack>
        <Tooltip
          label={translate(
            notification.readAt
              ? 'common:notificationCenter.markUnread'
              : 'common:notificationCenter.markRead'
          )}
        >
          <ActionIcon
            variant="subtle"
            aria-label={translate(
              notification.readAt
                ? 'common:notificationCenter.markUnread'
                : 'common:notificationCenter.markRead'
            )}
            onClick={() => onToggleRead(notification)}
          >
            {notification.readAt ? (
              <IconCircleDashed size={18} />
            ) : (
              <IconCircleCheck size={18} />
            )}
          </ActionIcon>
        </Tooltip>
      </Group>
    </Paper>
  );
};
